import { UserAchievementsType } from '@sportspredict/shared';
import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useNavigate, PrefetchPageLinks } from 'react-router';
import { useIsMobile } from '@/hooks';
import { cn } from '@/utils';
import { ACHIEVEMENT_COPY } from '@/constants';

export default function MilestoneModal({
	open,
	onClose,
	milestone,
	autoCloseMs = 10000,
	milestoneDetails,
}) {
	const [visible, setVisible] = useState(false);

	const cardRef = useRef(null);
	const timerRef = useRef(null);
	const isMobile = useIsMobile();
	const navigate = useNavigate();

	useEffect(() => {
		if (!open) return;
		if (milestone == null) return;

		setVisible(true);

		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		const onKey = (e) => {
			if (e.key === 'Escape') onClose?.('escape');
		};
		window.addEventListener('keydown', onKey);

		if (autoCloseMs > 0) {
			timerRef.current = setTimeout(() => onClose?.('timeout'), autoCloseMs);
		}

		return () => {
			document.body.style.overflow = prev;
			window.removeEventListener('keydown', onKey);
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, [open, autoCloseMs, onClose]);

	if (!open) return null;
	if (milestone == null) return null;

	const closeNow = (reason) => {
		setVisible(false);
		setTimeout(() => onClose?.(reason || 'close'), 150);
	};

	const copy = ACHIEVEMENT_COPY[milestone];

	let customMessage = null;

	if (milestone === UserAchievementsType.SlateWinner && milestoneDetails) {
		customMessage = (
			<>
				You’ve <b>perfectly predicted</b> all markets in a single event slate.
				<br />
				<b>
					<i>
						{milestoneDetails?.totalMarketLengthInSlate} out of{' '}
						{milestoneDetails.totalMarketLengthInSlate} correct trades in{' '}
						{milestoneDetails?.eventName}
						{milestoneDetails?.slateName ? `, ${milestoneDetails.slateName}` : ''}
					</i>
				</b>
			</>
		);
	}

	return (
		<div
			className="fixed inset-0 z-9999 flex items-center justify-center"
			role="dialog"
			aria-modal="true"
		>
			{copy.customButton?.redirectTo && <PrefetchPageLinks page={copy.customButton.redirectTo} />}
			{/* Backdrop */}
			<button
				aria-label="Close"
				onClick={() => closeNow('backdrop')}
				className="absolute inset-0 bg-black/60"
			/>

			{/* Card */}
			<div
				ref={cardRef}
				className={cn(
					`relative mx-4 w-full rounded-lg border border-[#2D3A6C]
          bg-[#1E2748] p-4 sm:p-12 text-center shadow-2xl
          transition-all duration-200 ease-out
          ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`,
					isMobile && 'max-w-[345px] h-[446px]',
					!isMobile && 'max-w-[458px] h-[462px]'
				)}
			>
				{/* Close */}
				<button
					onClick={() => onClose?.('x')}
					aria-label="Close"
					className="absolute right-2 top-2 rounded-full p-1 text-[#D4D5D8]"
				>
					<X className="h-6 w-6" />
				</button>

				{/* Icon */}
				<div className="relative mx-auto mb-4 mt-6 h-40 w-40 flex items-center justify-center">
					{copy.icon?.endsWith('.webm') ? (
						<video
							src={copy.icon}
							autoPlay
							loop
							muted
							playsInline
							className="h-full w-full object-contain rounded-full"
						/>
					) : (
						<img src={copy.icon} alt="" className="h-full w-full object-contain rounded-full" />
					)}
				</div>

				<h2 className="text-[#F7F7F8] text-3xl sm:text-4xl font-bold leading-9">{copy.title}</h2>

				<p className="mt-3 text-[#D4D5D8] text-sm font-normal">
					{customMessage ? customMessage : copy.message}
				</p>

				<p className="text-[#D4D5D8] text-sm font-normal mb-8">{copy.followUpMessage}</p>

				{/* Button */}
				<button
					onClick={() => {
						if (copy.customButton?.redirectTo) {
							navigate(copy.customButton.redirectTo);
						}
						onClose?.('continue');
					}}
					className="inline-flex items-center justify-center w-full rounded-[30px]
                     bg-blue-custom-2 text-white font-semibold py-2.5 hover:opacity-95
                     transition-opacity duration-150"
				>
					{copy.customButton?.text || 'Continue'}
				</button>
			</div>
		</div>
	);
}
